import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Edit3, Trash2, Search, ChevronLeft, ChevronRight, GraduationCap } from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import useGlobale from '../components/global';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import DemandeurMenu from '../components/DemandeurMenu';
import Select from 'react-select';
import countryList from 'react-select-country-list';

const FormationPage = () => {
    const { user } = useGlobale();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [formations, setFormations] = useState([]);
    const [typesFormation, setTypesFormation] = useState([]);
    const [domainesFormation, setDomainesFormation] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedFormation, setSelectedFormation] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    
    // États pour la recherche et pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(6);
    
    // Liste des pays memoized
    const countries = useMemo(() => countryList().getData(), []);
    const nigerOption = countries.find(country => country.value === 'NE') || { value: 'NE', label: 'Niger' };
    
    // État pour le formulaire
    const [formData, setFormData] = useState({
        nom: '',
        date_debut: '',
        date_fin: '',
        type_formation: '',
        domaine_formation: '',
        pays: 'Niger',
        descrition: ''
    });
    
    // État pour les erreurs
    const [errors, setErrors] = useState({});
    const [showOtherTypeFormation, setShowOtherTypeFormation] = useState(false);
    const [showOtherDomaineFormation, setShowOtherDomaineFormation] = useState(false);

    // Set sidebar initial state based on screen size
    useEffect(() => {
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 1024);
        };
        
        // Set initial state
        handleResize();
        
        // Add event listener
        window.addEventListener('resize', handleResize);
        
        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch data when component mounts
    useEffect(() => {
        fetchFormations();
        fetchTypesFormation();
        fetchDomainesFormation();
    }, []);

    const fetchFormations = async () => {
        setLoading(true);
        try {
            const response = await api.get('demandeur/formations/');
            setFormations(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des formations:', error);
            toast.error('Erreur lors de la récupération de vos formations');
        } finally {
            setLoading(false);
        }
    };

    const fetchTypesFormation = async () => {
        try {
            const response = await api.get('demandeur/types-formation/');
            setTypesFormation(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des types de formation:', error);
            toast.error('Erreur lors de la récupération des types de formation');
        }
    };

    const fetchDomainesFormation = async () => {
        try {
            const response = await api.get('demandeur/domaines-formation/');
            setDomainesFormation(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des domaines de formation:', error);
            toast.error('Erreur lors de la récupération des domaines de formation');
        }
    };

    // Fonction pour formater la date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric'
        });
    };

    // Fonction pour calculer la durée
    const calculateDuration = (startDate, endDate) => {
        if (!startDate) return '-';
        
        const start = new Date(startDate);
        const end = endDate ? new Date(endDate) : new Date();
        
        const diffTime = Math.abs(end - start);
        const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
        
        if (diffMonths < 12) {
            return `${diffMonths} mois`;
        }
        
        const years = Math.floor(diffMonths / 12);
        const months = diffMonths % 12;
        
        if (months === 0) {
            return `${years} an${years > 1 ? 's' : ''}`;
        }
        
        return `${years} an${years > 1 ? 's' : ''} et ${months} mois`;
    };

    // Fonction pour filtrer les données selon le terme de recherche
    const getFilteredData = () => {
        if (!searchTerm.trim()) return formations;
        
        return formations.filter(formation => 
            formation.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (formation.descrition && formation.descrition.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    };

    // Fonction pour obtenir les données paginées
    const getPaginatedData = () => {
        const filteredData = getFilteredData();
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(startIndex, startIndex + itemsPerPage);
    };

    // Calculer le nombre total de pages
    const totalPages = Math.ceil(getFilteredData().length / itemsPerPage);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            
            // Gérer l'affichage des champs "autre"
            if (name === 'type_formation') {
                const selectedType = typesFormation.find(t => t.id === parseInt(value));
                setShowOtherTypeFormation(selectedType?.libelle.toLowerCase() === 'autre');
                if (selectedType?.libelle.toLowerCase() !== 'autre') {
                    newData.autre_type_formation = '';
                }
            }
            
            if (name === 'domaine_formation') {
                const selectedDomaine = domainesFormation.find(d => d.id === parseInt(value));
                setShowOtherDomaineFormation(selectedDomaine?.nom.toLowerCase() === 'autre');
                if (selectedDomaine?.nom.toLowerCase() !== 'autre') {
                    newData.autre_domaine_formation = '';
                }
            }
            
            return newData;
        });
        
        // Effacer l'erreur lorsqu'un champ est modifié
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Réinitialiser à la première page lors de la recherche
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1); // Réinitialiser à la première page lors du changement d'items par page
    };

    // Fonction pour gérer le changement du pays via le select
    const handleCountryChange = (selectedOption) => {
        setFormData({ ...formData, pays: selectedOption.label });
        if (errors.pays) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.pays;
                return newErrors;
            });
        }
    };

    // Fonctions pour la gestion du CRUD
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.nom.trim()) {
            newErrors.nom = "Le nom de la formation est requis";
        }
        
        if (!formData.date_debut) {
            newErrors.date_debut = "La date de début est requise";
        }
        
        if (!formData.type_formation) {
            newErrors.type_formation = "Le type de formation est requis";
        }
        
        if (!formData.domaine_formation) {
            newErrors.domaine_formation = "Le domaine de formation est requis";
        }
        
        if (!formData.pays.trim()) {
            newErrors.pays = "Le pays est requis";
        }
        
        if (!formData.descrition.trim()) {
            newErrors.descrition = "La description est requise";
        }
        
        if (showOtherTypeFormation && !formData.autre_type_formation?.trim()) {
            newErrors.autre_type_formation = "Veuillez préciser le type de formation";
        }
        
        if (showOtherDomaineFormation && !formData.autre_domaine_formation?.trim()) {
            newErrors.autre_domaine_formation = "Veuillez préciser le domaine de formation";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        try {
            let response;
            if (isEditMode && selectedFormation) {
                response = await api.put(`demandeur/formations/${selectedFormation.id}/`, {
                    ...formData,
                    utlisateur: user.id,
                });
                toast.success('Formation mise à jour avec succès');
            } else {
                response = await api.post('demandeur/formations/', {
                    ...formData,
                    utlisateur: user.id,
                });
                toast.success('Formation ajoutée avec succès');
            }
            
            // Mise à jour de la liste des formations
            fetchFormations();
            setShowModal(false);
        } catch (error) {
            console.error('Erreur lors de la sauvegarde de la formation:', error);
            
            if (error.response && error.response.data) {
                if (typeof error.response.data === 'object') {
                    setErrors(error.response.data);
                } else {
                    toast.error('Erreur lors de la sauvegarde de la formation');
                }
            } else {
                toast.error('Erreur de connexion au serveur');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            await api.delete(`demandeur/formations/${id}/`);
            toast.success('Formation supprimée avec succès');
            setDeleteConfirmation(null);
            // Mettre à jour la liste des formations
            fetchFormations();
        } catch (error) {
            console.error('Erreur lors de la suppression de la formation:', error);
            toast.error('Erreur lors de la suppression de la formation');
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (formation) => {
        setIsEditMode(true);
        setSelectedFormation(formation);
        setFormData({
            nom: formation.nom,
            date_debut: formation.date_debut,
            date_fin: formation.date_fin || '',
            type_formation: formation.type_formation,
            domaine_formation: formation.domaine_formation,
            pays: formation.pays,
            descrition: formation.descrition
        });
        setShowModal(true);
    };

    const openAddModal = () => {
        setIsEditMode(false);
        setSelectedFormation(null);
        setFormData({
            nom: '',
            date_debut: '',
            date_fin: '',
            type_formation: '',
            domaine_formation: '',
            pays: 'Niger',
            descrition: ''
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setErrors({});
    };

    // Interface utilisateur - à compléter dans la prochaine étape
    const renderSearch = () => {
        return (
            <div className="mb-6">
                <div className="relative w-full sm:w-64 md:w-80">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search size={16} className="text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="Rechercher une formation..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>
        );
    };

    const renderContent = () => {
        if (loading && formations.length === 0) {
            return (
                <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
                </div>
            );
        }

        return (
            <>
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-[#437011] mb-2">
                            <GraduationCap className="inline-block mr-2" />
                            Formations
                        </h1>
                        <p className="text-gray-600">Gérez vos formations et parcours éducatifs</p>
                    </div>
                    <button
                        onClick={openAddModal}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-orange-700 transition mt-4 md:mt-0 w-full md:w-auto justify-center"
                    >
                        <Plus size={18} className="mr-2" />
                        Ajouter une formation
                    </button>
                </div>

                {renderSearch()}
                
                {formations.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-8 text-center">
                        <div className="mb-6">
                            <div className="h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 mx-auto">
                                <GraduationCap size={36} strokeWidth={1.5} />
                            </div>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">Aucune formation</h3>
                        <p className="text-gray-600 mb-6 max-w-md mx-auto">
                            Ajoutez vos formations pour mettre en valeur votre parcours éducatif auprès des recruteurs.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {getPaginatedData().map(formation => {
                                const typeFormation = typesFormation.find(t => t.id === formation.type_formation);
                                const domaineFormation = domainesFormation.find(d => d.id === formation.domaine_formation);
                                
                                // Générer une couleur pastel aléatoire mais consistante pour chaque type de formation
                                const typeId = formation.type_formation || 0;
                                const hue = (typeId * 137) % 360; // Utiliser un nombre premier pour une distribution uniforme
                                const bgColor = `hsla(${hue}, 70%, 85%, 0.2)`;
                                const accentColor = `hsla(${hue}, 70%, 45%, 1)`;
                                
                                return (
                                    <div 
                                        key={formation.id} 
                                        className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition duration-300 transform hover:-translate-y-1"
                                        style={{ borderTop: `4px solid ${accentColor}` }}
                                    >
                                        <div className="p-6">
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex items-center">
                                                    <div 
                                                        className="p-3 rounded-full mr-3" 
                                                        style={{ backgroundColor: bgColor }}
                                                    >
                                                        <GraduationCap className="h-6 w-6" style={{ color: accentColor }} />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-semibold text-gray-800">{formation.nom}</h3>
                                                        <p className="text-sm text-gray-600">
                                                            {typeFormation ? typeFormation.libelle : 'Type non spécifié'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex space-x-1">
                                                    <button 
                                                        onClick={() => openEditModal(formation)}
                                                        className="bg-orange-50 hover:bg-orange-100 text-orange-600 p-2 rounded-full transition-colors duration-200"
                                                        title="Modifier"
                                                    >
                                                        <Edit3 size={16} />
                                                    </button>
                                                    <button 
                                                        onClick={() => setDeleteConfirmation(formation.id)}
                                                        className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-full transition-colors duration-200"
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div className="mt-4 space-y-3">
                                                <div className="flex items-start">
                                                    <span className="text-sm font-medium text-gray-500 w-28">Domaine:</span>
                                                    <span className="text-sm text-gray-700 flex-1">
                                                        {domaineFormation ? domaineFormation.nom : 'Non spécifié'}
                                                    </span>
                                                </div>
                                                
                                                <div className="flex items-start">
                                                    <span className="text-sm font-medium text-gray-500 w-28">Période:</span>
                                                    <span className="text-sm text-gray-700 flex-1">
                                                        {formatDate(formation.date_debut)} {formation.date_fin ? `- ${formatDate(formation.date_fin)}` : '- Présent'}
                                                    </span>
                                                </div>
                                                
                                                <div className="flex items-start">
                                                    <span className="text-sm font-medium text-gray-500 w-28">Durée:</span>
                                                    <span className="text-sm text-gray-700 flex-1">
                                                        {calculateDuration(formation.date_debut, formation.date_fin)}
                                                    </span>
                                                </div>
                                                
                                                <div className="flex items-start">
                                                    <span className="text-sm font-medium text-gray-500 w-28">Pays:</span>
                                                    <span className="text-sm text-gray-700 flex-1">
                                                        {formation.pays}
                                                    </span>
                                                </div>
                                                
                                                <div className="pt-3 mt-3 border-t border-gray-100">
                                                    <p className="text-sm text-gray-600 line-clamp-3">
                                                        {formation.descrition}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="mt-6 flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                    <span className="text-sm text-gray-700">
                                        Afficher
                                    </span>
                                    <select
                                        value={itemsPerPage}
                                        onChange={handleItemsPerPageChange}
                                        className="border border-gray-300 rounded-md py-1 px-2 text-sm"
                                    >
                                        <option value={6}>6</option>
                                        <option value={9}>9</option>
                                        <option value={12}>12</option>
                                        <option value={24}>24</option>
                                    </select>
                                    <span className="text-sm text-gray-700">
                                        par page
                                    </span>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    
                                    <span className="text-sm text-gray-700">
                                        Page {currentPage} sur {totalPages}
                                    </span>
                                    
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </>
        );
    };

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={DemandeurMenu()}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1 flex flex-col w-full">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
                    {renderContent()}
                </main>
            </div>

            {/* Modal pour ajouter/éditer une formation */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {isEditMode ? "Modifier la formation" : "Ajouter une formation"}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nom de la formation <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="nom"
                                    value={formData.nom}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2 border ${errors.nom ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    placeholder="Ex: Licence en informatique, Formation en développement web, etc."
                                />
                                {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Date de début <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="date_debut"
                                        value={formData.date_debut}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.date_debut ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    />
                                    {errors.date_debut && <p className="mt-1 text-sm text-red-600">{errors.date_debut}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Date de fin <span className="text-gray-400 text-xs">(laissez vide si en cours)</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="date_fin"
                                        value={formData.date_fin}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.date_fin ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    />
                                    {errors.date_fin && <p className="mt-1 text-sm text-red-600">{errors.date_fin}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Type de formation <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="type_formation"
                                        value={formData.type_formation}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.type_formation ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    >
                                        <option value="">Sélectionner un type</option>
                                        {typesFormation.map(type => (
                                            <option key={type.id} value={type.id}>{type.libelle}</option>
                                        ))}
                                    </select>
                                    {errors.type_formation && <p className="mt-1 text-sm text-red-600">{errors.type_formation}</p>}
                                    
                                    {showOtherTypeFormation && (
                                        <div className="mt-2">
                                            <input
                                                type="text"
                                                name="autre_type_formation"
                                                value={formData.autre_type_formation || ''}
                                                onChange={handleInputChange}
                                                placeholder="Précisez le type de formation"
                                                className={`w-full px-4 py-2 border ${errors.autre_type_formation ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                            />
                                            {errors.autre_type_formation && (
                                                <p className="mt-1 text-sm text-red-600">{errors.autre_type_formation}</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Domaine de formation <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="domaine_formation"
                                        value={formData.domaine_formation}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.domaine_formation ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    >
                                        <option value="">Sélectionner un domaine</option>
                                        {domainesFormation.map(domaine => (
                                            <option key={domaine.id} value={domaine.id}>{domaine.nom}</option>
                                        ))}
                                    </select>
                                    {errors.domaine_formation && <p className="mt-1 text-sm text-red-600">{errors.domaine_formation}</p>}
                                    
                                    {showOtherDomaineFormation && (
                                        <div className="mt-2">
                                            <input
                                                type="text"
                                                name="autre_domaine_formation"
                                                value={formData.autre_domaine_formation || ''}
                                                onChange={handleInputChange}
                                                placeholder="Précisez le domaine de formation"
                                                className={`w-full px-4 py-2 border ${errors.autre_domaine_formation ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                            />
                                            {errors.autre_domaine_formation && (
                                                <p className="mt-1 text-sm text-red-600">{errors.autre_domaine_formation}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Pays <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    options={countries}
                                    value={countries.find(country => country.label === formData.pays) || nigerOption}
                                    onChange={handleCountryChange}
                                    className={errors.pays ? 'border-red-500' : ''}
                                    placeholder="Sélectionnez un pays"
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderColor: errors.pays ? '#ef4444' : '#d1d5db',
                                            boxShadow: errors.pays ? '0 0 0 1px #ef4444' : base.boxShadow,
                                            '&:hover': {
                                                borderColor: errors.pays ? '#ef4444' : '#f59e0b'
                                            }
                                        })
                                    }}
                                />
                                {errors.pays && <p className="mt-1 text-sm text-red-600">{errors.pays}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    name="descrition"
                                    value={formData.descrition}
                                    onChange={handleInputChange}
                                    rows={4}
                                    className={`w-full px-4 py-2 border ${errors.descrition ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                    placeholder="Décrivez votre formation, le contenu du programme, les compétences acquises, etc."
                                />
                                {errors.descrition && <p className="mt-1 text-sm text-red-600">{errors.descrition}</p>}
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`px-4 py-2 rounded-lg text-white ${loading ? 'bg-orange-400' : 'bg-orange-600 hover:bg-orange-700'}`}
                                >
                                    {loading ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Enregistrement...
                                        </span>
                                    ) : (
                                        isEditMode ? "Mettre à jour" : "Ajouter"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de confirmation de suppression */}
            {deleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirmer la suppression</h3>
                            <p className="text-gray-600 mb-6">
                                Êtes-vous sûr de vouloir supprimer cette formation ? Cette action est irréversible.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setDeleteConfirmation(null)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={() => handleDelete(deleteConfirmation)}
                                    className="px-4 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormationPage;
